import { Component } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { RoleHumanized, UserRole } from '../../../core/models/entities/role';

@Component({
  selector: 'app-permissions-page',
  template: `
    <div class="container relative">
      <h1 class="md:pt-8">Rôles et droits d'accès</h1>
    
      <div
        class="sticky top-14 mt-16 rounded-t-md border-x border-t border-secondary-500 bg-white p-4 px-4 lg:px-16 lg:pt-16"
        >
        <div class="px-8">
          <div
            class="grid grid-cols-2 items-center font-bold uppercase md:grid-cols-6"
            >
            <div>Rôle</div>
            <div class="hidden text-center md:block">{{
              RoleHumanized[UserRole.Member]
            }}</div>
            <div class="hidden text-center md:block">{{
              RoleHumanized[UserRole.MemberPlus]
            }}</div>
            <div class="hidden text-center md:block">{{
              RoleHumanized[UserRole.Finance]
            }}</div>
            <div class="hidden text-center md:block">{{
              RoleHumanized[UserRole.Staff]
            }}</div>
            <div class="hidden text-center md:block">{{
              RoleHumanized[UserRole.Admin]
            }}</div>
            <div class="text-center md:hidden">
              <app-select-input
                [options]="rolesOptions"
                [control]="roleControl"
                [marginBottom]="false"
              ></app-select-input>
            </div>
          </div>
        </div>
      </div>
    
      <div
        class="space-y-4 rounded-b-md border-x border-b border-secondary-500 px-4 pb-4 lg:px-16 lg:pb-16"
        >
        @for (module of moduleKeys; track module; let i = $index) {
          <div class="space-y-4">
            <div class="rounded-full bg-light px-8 py-6" [class.mt-16]="i > 0">
              <div class="grid grid-cols-2 items-center md:grid-cols-6">
                <div class="font-bold">{{ module }}</div>
                @if (moduleHasGlobalPermissions(module)) {
                  <div
                    [innerHtml]="content(module, UserRole.Member)"
                    class="hidden text-center md:block"
                  ></div>
                }
                @if (moduleHasGlobalPermissions(module)) {
                  <div
                    [innerHtml]="content(module, UserRole.MemberPlus)"
                    class="hidden text-center md:block"
                  ></div>
                }
                @if (moduleHasGlobalPermissions(module)) {
                  <div
                    [innerHtml]="content(module, UserRole.Finance)"
                    class="hidden text-center md:block"
                  ></div>
                }
                @if (moduleHasGlobalPermissions(module)) {
                  <div
                    [innerHtml]="content(module, UserRole.Staff)"
                    class="hidden text-center md:block"
                  ></div>
                }
                @if (moduleHasGlobalPermissions(module)) {
                  <div
                    [innerHtml]="content(module, UserRole.Admin)"
                    class="hidden text-center md:block"
                  ></div>
                }
                @if (moduleHasGlobalPermissions(module)) {
                  <div
                    class="text-center md:hidden"
                    [innerHtml]="content(module, roleControl.value)"
                  ></div>
                }
              </div>
            </div>
            @for (action of moduleActionsKeys(module); track action) {
              <div
                class="grid grid-cols-2 items-center px-8 py-4 md:grid-cols-6"
                >
                <div>{{ action }}</div>
                <div
                  [innerHtml]="actionContent(module, action, UserRole.Member)"
                  class="hidden text-center md:block"
                ></div>
                <div
                  [innerHtml]="actionContent(module, action, UserRole.MemberPlus)"
                  class="hidden text-center md:block"
                ></div>
                <div
                  [innerHtml]="actionContent(module, action, UserRole.Finance)"
                  class="hidden text-center md:block"
                ></div>
                <div
                  [innerHtml]="actionContent(module, action, UserRole.Staff)"
                  class="hidden text-center md:block"
                ></div>
                <div
                  [innerHtml]="actionContent(module, action, UserRole.Admin)"
                  class="hidden text-center md:block"
                ></div>
                <div
                  class="text-center md:hidden"
                  [innerHtml]="actionContent(module, action, roleControl.value)"
                ></div>
              </div>
            }
          </div>
        }
      </div>
    </div>
    `,
  standalone: false,
})
export class PermissionsPageComponent {
  UserRole = UserRole;
  RoleHumanized = RoleHumanized;
  rolesOptions = Object.values(UserRole).map((role) => ({
    label: RoleHumanized[role],
    value: role,
  }));
  roleControl = new UntypedFormControl(UserRole.Member);
  permissions = {
    Utilisateurs: {
      actions: {
        'Voir la liste': {
          [UserRole.Member]: true,
          [UserRole.MemberPlus]: true,
          [UserRole.Finance]: true,
          [UserRole.Staff]: true,
          [UserRole.Admin]: true,
        },
        Ajouter: {
          [UserRole.Staff]: 'uniquement un staff ou un membre',
          [UserRole.Admin]: 'tous les utilisateurs',
        },
        Modifier: {
          [UserRole.Member]: 'soi-même',
          [UserRole.MemberPlus]: 'soi-même',
          [UserRole.Finance]: 'soi-même',
          [UserRole.Staff]: 'uniquement un staff ou un membre',
          [UserRole.Admin]: 'tous les utilisateurs',
        },
        Supprimer: {
          [UserRole.Staff]: 'uniquement un staff ou un membre',
          [UserRole.Admin]: 'tous les utilisateurs',
        },
      },
    },
    Salles: {
      [UserRole.Admin]: true,
    },
    Réservations: {
      actions: {
        'Voir la liste': {
          [UserRole.Member]: true,
          [UserRole.MemberPlus]: true,
          [UserRole.Finance]: true,
          [UserRole.Staff]: true,
          [UserRole.Admin]: true,
        },
        'Ajouter (interne)': {
          [UserRole.Member]: true,
          [UserRole.MemberPlus]: true,
          [UserRole.Finance]: true,
          [UserRole.Staff]: true,
          [UserRole.Admin]: true,
        },
        'Ajouter (externe)': {
          [UserRole.MemberPlus]: true,
          [UserRole.Staff]: true,
          [UserRole.Admin]: true,
        },
        Modifier: {
          [UserRole.Member]: 'propres réservations',
          [UserRole.MemberPlus]: 'toutes les réservations',
          [UserRole.Finance]: 'propres réservations',
          [UserRole.Staff]: 'toutes les réservations',
          [UserRole.Admin]: 'toutes les réservations',
        },
        Annuler: {
          [UserRole.Member]: 'propres réservations',
          [UserRole.MemberPlus]: 'toutes les réservations',
          [UserRole.Finance]: 'propres réservations',
          [UserRole.Staff]: 'toutes les réservations',
          [UserRole.Admin]: 'toutes les réservations',
        },
        Exporter: {
          [UserRole.Member]: false,
          [UserRole.MemberPlus]: false,
          [UserRole.Finance]: false,
          [UserRole.Staff]: true,
          [UserRole.Admin]: true,
        },
      },
    },
    Réglages: {
      [UserRole.Member]: false,
      [UserRole.MemberPlus]: false,
      [UserRole.Finance]: false,
      [UserRole.Staff]: true,
      [UserRole.Admin]: true,
    },
    Facturation: {
      [UserRole.Finance]: true,
      [UserRole.Staff]: true,
      [UserRole.Admin]: true,
    },
    'Codes promotionnels': {
      actions: {
        'Voir la liste': {
          [UserRole.Staff]: true,
          [UserRole.Admin]: true,
        },
        Ajouter: {
          [UserRole.Staff]: true,
          [UserRole.Admin]: true,
        },
        'Activer / Désactiver': {
          [UserRole.Staff]: true,
          [UserRole.Admin]: true,
        },
        Supprimer: {
          [UserRole.Staff]: true,
          [UserRole.Admin]: true,
        },
      },
    },
    'Gestion du compte': {
      actions: {
        'Mon profil': {
          [UserRole.Member]: true,
          [UserRole.MemberPlus]: true,
          [UserRole.Finance]: true,
          [UserRole.Staff]: true,
          [UserRole.Admin]: true,
        },
        'Données de l’hôte': {
          [UserRole.Admin]: true,
        },
        'Choix de l’abonnement': {
          [UserRole.Admin]: true,
        },
        Sécurité: {
          [UserRole.Admin]: true,
        },
      },
    },
  };

  get moduleKeys() {
    return Object.keys(this.permissions);
  }

  actionContent(key: string, action: string, userRole: UserRole) {
    if (this.permissions[key]['actions'][action]?.[userRole] === true) {
      return '<i class="fa-solid fa-circle-check text-primary-500"></i>';
    } else if (
      typeof this.permissions[key]['actions'][action]?.[userRole] === 'string'
    ) {
      return this.permissions[key]['actions'][action][userRole];
    } else {
      return '-';
    }
  }

  content(key: string, userRole: UserRole) {
    if (this.permissions[key][userRole] === true) {
      return '<i class="fa-solid fa-circle-check text-primary-500"></i>';
    } else if (typeof this.permissions[key][userRole] === 'string') {
      return this.permissions[key][userRole];
    } else {
      return '-';
    }
  }

  moduleActionsKeys(key: string) {
    return this.permissions[key].actions
      ? Object.keys(this.permissions[key].actions)
      : [];
  }

  moduleHasGlobalPermissions(key: string) {
    return (
      !!this.permissions[key][UserRole.Member] ||
      !!this.permissions[key][UserRole.Staff] ||
      !!this.permissions[key][UserRole.Admin]
    );
  }
}
